import styled from 'styled-components';
import { ReactComponent as CrossIcon } from '../../icons/cross.svg'; // Adjust the import path as needed
import { colors } from '../../utils/colors'
import { fontFamily, purpleColors, violetColors, slateColors } from '../ContentPages/AppLandingPage/styles';


// Base TextButton with common styles
const BaseTextButton = styled.button`
  font-family: ${fontFamily};
  font-weight: 600;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  color: var(--Key, ${colors.key});
  leading-trim: both;
  text-edge: cap;
  cursor: pointer;
  background: none; /* Ensure the button has no background */
  border: none; /* Remove the default border */
  -webkit-tap-highlight-color: transparent;

  &:hover {
    color: var(--Key-darker, ${colors.keyDarker});
  }

  &:active {
    color: var(--Key-darkest, ${colors.keyDarkest});
  }

  &:disabled {
    color: ${colors.grey400};
    cursor: not-allowed;
  }
`;

// Specific TextButton sizes with typography styles
export const TextButtonXS = styled(BaseTextButton)`
  font-size: 12px;
`;

export const TextButtonS = styled(BaseTextButton)`
  font-size: 16px;
`;

export const TextButtonM = styled(BaseTextButton)`
  font-size: 24px;
`;

// Base Button with common styles
const BaseButton = styled.button`
  font-family: ${fontFamily};
  font-weight: 600;
  display: inline-flex;
  padding: 0.75rem 1rem;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.75rem;
  border: 2px solid ${slateColors[200]};
  background: ${slateColors[50]};
  overflow: hidden;
  color: ${slateColors[700]};
  leading-trim: both;
  text-edge: cap;
  text-overflow: ellipsis;
  cursor: pointer;
  transition: all 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${slateColors[100]};
    border-color: ${purpleColors[300]};
    color: ${slateColors[900]};
  }

  &:active {
    background: ${slateColors[200]};
    border-color: ${purpleColors[400]};
  }

  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
    box-shadow: 0 0 0 3px ${purpleColors[100]};
  }

  &:disabled {
    background: ${slateColors[50]};
    border: 2px solid ${slateColors[200]};
    color: ${slateColors[400]};
    cursor: not-allowed;
  }
`;

export const ButtonS = styled(BaseButton)`
  font-size: 16px;
`;

export const ButtonM = styled(BaseButton)`
  font-size: 24px;
  gap: 10px
`;

// Base CTA Button with common styles
const BaseCTAButton = styled.button`
  font-family: ${fontFamily};
  font-weight: 600;
  font-size: 1.125rem; 
  display: flex;
  align-self: stretch;
  padding: 1rem 2rem;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  border-radius: 0.75rem;
  leading-trim: both;
  text-edge: cap;
  width: 100%; /* Make the button fill the available width */
  cursor: pointer;
  transition: all 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

export const CTAButtonFilled = styled(BaseCTAButton)`
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  color: white;
  border: none;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);

  &:hover {
    background: linear-gradient(to right, ${purpleColors[700]}, ${violetColors[600]});
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: ${slateColors[300]};
    border: none;
    box-shadow: none;
    transform: none;
  }
`

export const CTAButtonHollow = styled(BaseCTAButton)`
  background: white;
  color: ${purpleColors[600]};
  border: 2px solid ${slateColors[200]};

  &:hover {
    background: ${slateColors[50]};
    border-color: ${purpleColors[300]};
    color: ${purpleColors[700]};
  }

  &:active {
    background: ${slateColors[100]};
    border-color: ${purpleColors[400]};
  }

  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
    box-shadow: 0 0 0 3px ${purpleColors[100]};
  }

  &:disabled {
    background: ${slateColors[50]};
    border: 2px solid ${slateColors[200]};
    color: ${slateColors[400]};
  }
`

const SegmentedControlContainer = styled.div`
  display: inline-flex;
  background: ${slateColors[50]};
  border: 2px solid ${slateColors[200]};
  border-radius: 0.75rem;
  padding: 0.25rem;
  gap: 0.25rem;
`;

const SegmentedControlButton = styled.button<{ $active: boolean; size?: 'small' | 'medium' }>`
  font-family: ${fontFamily};
  font-weight: 600;
  padding: ${props => props.size === 'small' ? '0.5rem 1rem' : '0.75rem 1.5rem'};
  font-size: ${props => props.size === 'small' ? '0.875rem' : '1rem'};
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active 
    ? `linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]})` 
    : 'transparent'};
  color: ${props => props.$active ? 'white' : slateColors[600]};
  box-shadow: ${props => props.$active 
    ? '0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)' 
    : 'none'};
  -webkit-tap-highlight-color: transparent;

  &:hover {
    color: ${props => props.$active ? 'white' : slateColors[700]};
    background: ${props => props.$active 
      ? `linear-gradient(to right, ${purpleColors[700]}, ${violetColors[600]})` 
      : slateColors[100]};
    box-shadow: ${props => props.$active 
      ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' 
      : 'none'};
  }

  &:active {
    transform: ${props => props.$active ? 'scale(0.98)' : 'scale(0.98)'};
  }

  &:focus {
    outline: none;
    ${props => !props.$active && `
      background: ${slateColors[100]};
    `}
    ${props => props.$active && `
      box-shadow: 0 0 0 3px ${purpleColors[100]}, 0 2px 4px -1px rgba(0, 0, 0, 0.1);
    `}
  }
`;

export const SegmentedControlM = <T extends string>({
    activeValue,
    options,
    onChange
  }: {
    activeValue: T;
    options: { label: string; value: T }[];
    onChange: (value: T) => void;
  }) => {
    return (
      <SegmentedControlContainer>
        {options.map((option) => (
          <SegmentedControlButton
            key={option.value}
            $active={activeValue === option.value}
            size="medium"
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </SegmentedControlButton>
        ))}
      </SegmentedControlContainer>
    );
};

export const SegmentedControlS = <T extends string>({
    activeValue,
    options,
    onChange
  }: {
    activeValue: T;
    options: { label: string; value: T }[];
    onChange: (value: T) => void;
  }) => {
    return (
      <SegmentedControlContainer>
        {options.map((option) => (
          <SegmentedControlButton
            key={option.value}
            $active={activeValue === option.value}
            size="small"
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </SegmentedControlButton>
        ))}
      </SegmentedControlContainer>
    );
};

// Default Close button
interface CloseButtonProps {
    onClick?: () => void;
    disabled?: boolean;
}

const CloseButtonBase = styled.button`
    font-family: ${fontFamily};
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;  
    background: ${slateColors[50]};
    color: ${slateColors[600]};
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    border: 2px solid ${slateColors[200]};
    border-radius: 0.5rem;
    transition: all 0.2s;
    -webkit-tap-highlight-color: transparent;

    &:hover {
    background: ${slateColors[100]};
    border-color: ${purpleColors[300]};
    color: ${slateColors[900]};
  }

  &:active {
    background: ${slateColors[200]};
    border-color: ${purpleColors[400]};
  }

  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
    box-shadow: 0 0 0 3px ${purpleColors[100]};
  }

  &:disabled {
    background: ${slateColors[50]};
    border-color: ${slateColors[200]};
    color: ${slateColors[400]};
    cursor: not-allowed;
  }

  svg {
    fill: currentColor; /* Ensure the SVG icon inherits the button text color */
  }
`

// Component that uses the styled button and accepts props
export const CloseButton: React.FC<CloseButtonProps> = ({ onClick, disabled }) => (
    <CloseButtonBase onClick={onClick} disabled={disabled}>
      <CrossIcon width={24} height={24} />
    </CloseButtonBase>
);